// ctxmenu.ts — the Bridge home's seam onto THE context-menu primitive
// (Styles x Themes, PR-3b). One instance, this surface's `.hc-menu` /
// `.hc-menu-item` chrome bound in; the behaviour is
// pi-stack/src/client/ctxmenu.ts.
//
// This surface was the REFERENCE MACHINE the primitive was generalised from:
// the WF-A audit measured 27/27 right-click sites already routed through the
// engine that used to live in this file, against the Helm's six-in-three and
// ten surfaces with nothing. Promoting it to pi-stack is what let the Helm
// adopt the same behaviour instead of growing a third menu engine — and it
// brought back the one thing this copy did NOT have: the editable-text
// ruling, encoded in `attach()` rather than left to each call site to
// remember.
//
// The item shape keeps `icon:` (not the primitive's `glyph:`) because 27 call
// sites name it that way and renaming them in the same PR that changes the
// engine would make any regression unattributable. The mapping is one line
// below, and it is the only difference.

import { icon } from './generated/icons.js';
import { createCtxMenu, type CtxMenuVocabulary, type CtxMenuItem as PrimItem } from 'pi-stack/client/ctxmenu.js';

export {
  CTX_HANDLED, EDITABLE_SELECTOR, isEditableTarget, defaultCtxItems,
  selectionWithin, copyPlainText, attachCtxHandler,
} from 'pi-stack/client/ctxmenu.js';
export type { CtxProviderResult, CtxAttachOptions } from 'pi-stack/client/ctxmenu.js';

export interface CtxMenuItem {
  label: string;
  icon: string;
  run?: () => void;
  /** Disabled items paint muted, never run, and carry the WHY: `note` is the
   * inline annotation (e.g. "Up to date"), `tip` the hover title. */
  disabled?: boolean;
  note?: string;
  tip?: string;
  /** Red hover treatment (Abort). Rides the destructive INTENT, so the ONE
   * confirm-destructive interaction arms the row exactly as it arms a
   * button. */
  danger?: boolean;
  sep?: boolean;
}

/** The Bridge home's menu chrome — the same tokens the account menu wears
 *  (no bare hexes, all 20 themes hold). */
export const HC_CTX_VOCAB: CtxMenuVocabulary = {
  id: 'hcCtxMenu',
  menuClass: 'hc-menu hc-ctxmenu',
  openClass: 'on',
  itemClass: 'hc-menu-item',
  sepClass: 'hc-menu-sep',
  labelClass: 'hc-menu-item-l',
  noteClass: 'hc-menu-item-n',
  dangerClass: 'hc-menu-danger',
  icon,
  clampW: 240,
  clampH: 260,
};

const menu = createCtxMenu(HC_CTX_VOCAB);

/** `icon:` -> the primitive's `glyph:`. The one shape difference. */
const toPrim = (it: CtxMenuItem): PrimItem => ({
  label: it.label,
  glyph: it.icon,
  run: it.run,
  disabled: it.disabled,
  note: it.note,
  tip: it.tip,
  danger: it.danger,
  sep: it.sep,
});

/** Wire the singleton once (mountHome does it; idempotent). */
export function mountCtxMenu(): void {
  menu.mount();
}

export function openCtxMenu(x: number, y: number, items: CtxMenuItem[]): void {
  menu.open(x, y, items.map(toPrim));
}

export function closeCtxMenu(): void {
  menu.close();
}

/** Test seam: the singleton element (null before first mount). */
export function ctxMenuEl(): HTMLElement | null {
  return menu.el();
}

/**
 * Attach the menu to a surface root — the editable-text ruling included.
 * With no provider the root gets the DEFAULT text menu.
 */
export function attachCtxMenu(
  root: Element | Document,
  provider?: (ev: MouseEvent) => CtxMenuItem[] | null | undefined | 'handled',
  opts?: { noDefault?: boolean },
): () => void {
  return menu.attach(
    root,
    provider
      ? (ev) => {
          const got = provider(ev);
          if (got === 'handled' || got === null || got === undefined) return got;
          return got.map(toPrim);
        }
      : undefined,
    opts,
  );
}
